/**
 * EarningsWeekDetail — per-vehicle earnings breakdown for a single week.
 *
 * Navigated to from EarningsRecords when clicking a weekly row.
 * Receives `?week=YYYY-MM-DD` (Monday of the week) via search params.
 *
 * Reuses `get_partner_earnings_explorer` RPC with `p_group_by = 'vehicle'`
 * scoped to the week's date range.
 *
 * Features:
 * - Breadcrumb navigation back to Earnings
 * - Stat cards (Gross, Fees, Earnings, Retention) for the week
 * - Vehicle table with reg number, VIN, fee split bar
 * - Client-side search + pagination (small dataset per week)
 * - Mobile card layout
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Card, CardContent } from '@/components/ui/card';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { businessWeekBounds, formatCurrency, CHART_COLORS } from '@/lib/format';
import { getISOWeek, format } from 'date-fns';
import { captureError } from '@/lib/logger';
import { ListPagination } from '@/components/ListPagination';
import SectionDrawer from '@/components/navigation/SectionDrawer';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { Search } from 'lucide-react';

const PAGE_SIZE = 15;

function getSbtPhotoUrl(listingUrl: string | null, updatedAt?: string | null): string | null {
    if (!listingUrl) return null;
    const match = listingUrl.match(/sbtjapan\.com\/used-cars\/([A-Z0-9]+)/i);
    if (!match) return null;
    const cacheBust = updatedAt ? `?v=${new Date(updatedAt).getTime()}` : '';
    return `https://img.sbtjapan.com/img/carphoto/${match[1]}/normal/f.jpg${cacheBust}`;
}

interface EarningsRow {
    group_key: string;
    group_label: string;
    vehicle_count: number;
    gross_repayments: number;
    management_fee_amount: number;
    partner_portion: number;
    maintenance_cost: number;
    net_earnings: number;
    repayment_count: number;
}

interface VehicleInfo {
    id: number;
    make: string;
    model: string;
    color: string | null;
    updated_at: string | null;
    vehicle_registrations: { number: string | null } | { number: string | null }[] | null;
    vehicle_procurement: { listing_url: string | null }[] | null;
}

/** Inline horizontal stacked bar — partner% vs fee% */
function FeeBar({ gross, partnerPortion }: { gross: number; partnerPortion: number }) {
    if (gross <= 0) return <div className="h-2 rounded-full bg-muted w-full" />;
    const partnerPct = (partnerPortion / gross) * 100;
    return (
        <div className="flex items-center gap-2 w-full">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden flex">
                <div
                    className="h-full rounded-l-full transition-all duration-300"
                    style={{ width: `${partnerPct}%`, backgroundColor: CHART_COLORS.slate900 }}
                />
                <div
                    className="h-full rounded-r-full transition-all duration-300"
                    style={{ width: `${100 - partnerPct}%`, backgroundColor: CHART_COLORS.slate300 }}
                />
            </div>
            <span className="text-xs text-muted-foreground tabular-nums w-10 text-right shrink-0">
                {partnerPct.toFixed(2)}%
            </span>
        </div>
    );
}

export default function EarningsWeekDetail() {
    const { partnerId, signOut } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const weekParam = searchParams.get('week');
    // `?week=` is a 'yyyy-MM-dd' Monday; absent, it means the current Ghana week.
    const { weekStart, weekEnd, fromInstant, toInstant } = businessWeekBounds(weekParam);
    const weekNumber = getISOWeek(weekStart);

    const handleSignOut = async () => { await signOut(); navigate('/auth'); };
    const handleNavChange = (section: string) => { navigate(`/${section}`); };

    // ── Data: vehicle-level earnings for this week ──────────────────────
    const { data: earningsData, isLoading: earningsLoading } = useQuery({
        queryKey: ['partner-earnings-week-vehicles', partnerId, fromInstant, toInstant],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_earnings_explorer', {
                p_partner_id: partnerId!,
                p_from_date: fromInstant,
                p_to_date: toInstant,
                p_vehicle_ids: null,
                p_group_by: 'vehicle',
            });
            if (error) { captureError(error); throw error; }
            return data as EarningsRow[];
        },
        enabled: !!partnerId,
    });

    // ── Data: vehicle details for reg number / photo display ─────────────
    const { data: vehicleMap } = useQuery({
        queryKey: ['partner-vehicle-details', partnerId],
        queryFn: async () => {
            // Fetch vehicles + registrations
            const { data: vehicles, error } = await supabase
                .from('vehicles')
                .select('id, make, model, color, updated_at, vehicle_registrations(number)')
                .eq('partner_id', partnerId!);
            if (error) { captureError(error); throw error; }

            // Fetch procurement data separately (avoids PostgREST embedded RLS issues)
            const vehicleIds = (vehicles || []).map((v: any) => v.id);
            let procMap = new Map<number, string>();
            if (vehicleIds.length > 0) {
                const { data: procData } = await supabase
                    .from('vehicle_procurement')
                    .select('vehicle_id, listing_url')
                    .in('vehicle_id', vehicleIds);
                (procData || []).forEach((p: any) => {
                    if (p.listing_url) procMap.set(p.vehicle_id, p.listing_url);
                });
            }

            const map = new Map<string, VehicleInfo>();
            (vehicles || []).forEach((v: any) => {
                const info: VehicleInfo = {
                    ...v,
                    vehicle_procurement: procMap.has(v.id) ? [{ listing_url: procMap.get(v.id)! }] : null,
                };
                map.set(String(v.id), info);
            });
            return map;
        },
        enabled: !!partnerId,
    });

    // ── Computed: totals for stat cards ──────────────────────────────────
    const totals = useMemo(() => {
        if (!earningsData || earningsData.length === 0) return null;
        const gross = earningsData.reduce((s, r) => s + r.gross_repayments, 0);
        const fees = earningsData.reduce((s, r) => s + r.management_fee_amount, 0);
        const earnings = earningsData.reduce((s, r) => s + r.partner_portion, 0);
        const count = earningsData.reduce((s, r) => s + r.repayment_count, 0);
        return { gross, fees, earnings, avgRate: gross > 0 ? (fees / gross * 100) : 0, count };
    }, [earningsData]);

    // ── Computed: client-side search filtering ──────────────────────────
    const filteredData = useMemo(() => {
        if (!earningsData) return [];
        if (!search) return earningsData;
        const q = search.toLowerCase();
        return earningsData.filter(row => {
            const vehicle = vehicleMap?.get(row.group_key);
            const reg = vehicle?.vehicle_registrations;
            const regNumber = Array.isArray(reg) ? reg[0]?.number || '' : reg?.number || '';
            const make = vehicle?.make || '';
            const model = vehicle?.model || '';
            return (
                regNumber.toLowerCase().includes(q) ||
                make.toLowerCase().includes(q) ||
                model.toLowerCase().includes(q) ||
                row.group_label.toLowerCase().includes(q)
            );
        });
    }, [earningsData, vehicleMap, search]);

    // Reset page on filter changes
    useEffect(() => { setCurrentPage(1); }, [search]);

    // ── Computed: pagination ────────────────────────────────────────────
    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredData.slice(start, start + PAGE_SIZE);
    }, [filteredData, currentPage]);

    /** Render vehicle identity: reg number (primary) + VIN (secondary) */
    function VehicleCell({ row }: { row: EarningsRow }) {
        const vehicle = vehicleMap?.get(row.group_key);
        const reg = vehicle?.vehicle_registrations;
        const regNumber = Array.isArray(reg) ? reg[0]?.number : reg?.number;
        const listingUrl = Array.isArray(vehicle?.vehicle_procurement) ? vehicle.vehicle_procurement[0]?.listing_url : null;
        const photoUrl = getSbtPhotoUrl(listingUrl ?? null, vehicle?.updated_at);

        return (
            <div className="flex items-center gap-2">
                {photoUrl ? (
                    <HoverCard openDelay={200} closeDelay={100}>
                        <HoverCardTrigger asChild>
                            <img
                                src={photoUrl}
                                alt=""
                                className="h-8 w-8 rounded-md border border-gray-200 object-cover shrink-0 cursor-zoom-in"
                                onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }}
                            />
                        </HoverCardTrigger>
                        <HoverCardContent side="right" align="center" className="w-auto p-1" sideOffset={8}>
                            <img src={photoUrl} alt={regNumber || ''} className="w-[480px] rounded-md" />
                        </HoverCardContent>
                    </HoverCard>
                ) : null}
                <div
                    className={`h-8 w-8 rounded-md border border-gray-200 shrink-0${photoUrl ? ' hidden' : ''}`}
                    style={{ backgroundColor: vehicle?.color || '#e5e7eb' }}
                />
                <div>
                    <span className="font-medium">{regNumber || row.group_label}</span>
                    {(vehicle?.make || vehicle?.model) && (
                        <p className="text-xs text-muted-foreground">{[vehicle?.make, vehicle?.model].filter(Boolean).join(' ')}</p>
                    )}
                </div>
            </div>
        );
    }

    // ── Render ──────────────────────────────────────────────────────────
    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            {/* Mobile nav */}
            <div className="lg:hidden">
                <SectionDrawer
                    activeSection="finance/earnings"
                    onSectionChange={handleNavChange}
                    onSignOut={handleSignOut}
                />
            </div>

            {/* Header */}
            <div>
                <Breadcrumbs items={[
                    { label: 'Earnings', href: '/finance/earnings' },
                    { label: `Week ${weekNumber}` },
                ]} />
                <h1 className="text-3xl font-semibold tracking-tight mb-2">
                    {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
                </h1>
                <p className="text-muted-foreground">
                    Week {weekNumber} · Vehicle breakdown
                </p>
            </div>

            {/* Stat Cards */}
            {earningsLoading ? (
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
            ) : totals ? (
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                    <StatCard title="Gross Repayments" value={<CurrencyDisplay amount={totals.gross} />} description={`${totals.count} repayments`} />
                    <StatCard title="Management Fees" value={<CurrencyDisplay amount={totals.fees} />} description={`Avg rate: ${totals.avgRate.toFixed(2)}%`} />
                    <StatCard title="Your Earnings" value={<CurrencyDisplay amount={totals.earnings} />} description="After management fees" />
                    <StatCard title="Retention Rate" value={`${(totals.gross > 0 ? (totals.earnings / totals.gross * 100) : 0).toFixed(2)}%`} description="Your share of gross" />
                </div>
            ) : null}

            {/* Search + Pagination */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="relative w-full sm:w-72">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search by reg, VIN, make..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                {totalPages > 1 && (
                    <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onPageChange={setCurrentPage}
                    />
                )}
            </div>

            {/* Content */}
            {earningsLoading ? (
                <div className="space-y-3">
                    {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
            ) : filteredData.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground">
                    {search ? 'No vehicles match your search.' : 'No earnings data for this week.'}
                </div>
            ) : (
                <>
                    {/* Desktop Table */}
                    <div className="overflow-x-auto hidden lg:block">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[200px]">Vehicle</TableHead>
                                    <TableHead className="text-right">Count</TableHead>
                                    <TableHead className="text-right">Gross</TableHead>
                                    <TableHead className="w-[200px]">Split</TableHead>
                                    <TableHead className="text-right">Fee</TableHead>
                                    <TableHead className="text-right">Your Earnings</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {paginatedData.map((row) => {
                                    const feeRate = row.gross_repayments > 0
                                        ? (row.management_fee_amount / row.gross_repayments * 100)
                                        : 0;
                                    return (
                                        <TableRow key={row.group_key}>
                                            <TableCell><VehicleCell row={row} /></TableCell>
                                            <TableCell className="text-right tabular-nums">{row.repayment_count}</TableCell>
                                            <TableCell className="text-right tabular-nums">{formatCurrency(row.gross_repayments)}</TableCell>
                                            <TableCell>
                                                <FeeBar gross={row.gross_repayments} partnerPortion={row.partner_portion} />
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="text-sm tabular-nums text-muted-foreground">
                                                    {formatCurrency(row.management_fee_amount)}
                                                    <span className="ml-1 text-xs">({feeRate.toFixed(2)}%)</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right font-medium tabular-nums">
                                                {formatCurrency(row.partner_portion)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Mobile Card Layout */}
                    <div className="lg:hidden space-y-3">
                        {paginatedData.map((row) => {
                            const feeRate = row.gross_repayments > 0
                                ? (row.management_fee_amount / row.gross_repayments * 100)
                                : 0;
                            return (
                                <Card key={row.group_key} className="border border-border/40">
                                    <CardContent className="p-4 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <VehicleCell row={row} />
                                            <div className="text-xs text-muted-foreground">
                                                {row.repayment_count} repayments
                                            </div>
                                        </div>
                                        <FeeBar gross={row.gross_repayments} partnerPortion={row.partner_portion} />
                                        <div className="grid grid-cols-3 gap-2">
                                            <div>
                                                <div className="text-xs text-muted-foreground">Gross</div>
                                                <div className="text-sm font-medium tabular-nums">{formatCurrency(row.gross_repayments)}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-muted-foreground">Fee ({feeRate.toFixed(2)}%)</div>
                                                <div className="text-sm text-muted-foreground tabular-nums">{formatCurrency(row.management_fee_amount)}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-xs text-muted-foreground">Yours</div>
                                                <div className="text-sm font-semibold tabular-nums">{formatCurrency(row.partner_portion)}</div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
